import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { titleMRClass } from "src/components/common/common";
import { createPortal } from "react-dom";
import DatePicker from "src/components/common/calendar/DatePicker";
import { formatDate } from "src/utils/string/stringUtil";
import { handleKeyDown } from "src/utils/commonForm/commonFormUtils";

// 시간 포맷팅 함수 (24시간 형식)
const formatTime = (date: Date, includeSeconds: boolean = false): string => {
	const hours = date.getHours().toString().padStart(2, '0');
	const minutes = date.getMinutes().toString().padStart(2, '0');
	const seconds = includeSeconds ? date.getSeconds().toString().padStart(2, '0') : '';

	return includeSeconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`;
};

interface Props {
	title?: string;
	value: Date | undefined;
	setValue: (value: Date | undefined) => void;
	editable: boolean;
	titleless?: boolean;
	visible: boolean;
	setVisible: (visible: boolean) => void;
	name?: string;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	wrapperOption?: {
		wrapperStyle: string;
		titleStyle: string;
		inputStyle: string;
		timeInputStyle?: string;
	}
	pickerPos?: {top: number, left: number, height: number};
	setPickerPos?: (pickerPos: {top: number, left: number, height: number}) => void;
	resetType?: 'today' | 'clear';
	yearMonthVisible?: boolean;
	placeholder?: string;
	showSeconds?: boolean; // 초 표시 여부
	hideTime?: boolean; // Info 폼의 시간 입력 UI 숨김
	hidePickerTime?: boolean; // 팝업 내부 시간 입력 UI 숨김
}

export const DateTimeForm = (props: Props) => {
	const { 
		title,
		value, setValue,
		editable,
		titleless = false,
		visible, setVisible,
		name,	
		onChange,
		wrapperOption = {
			wrapperStyle: "w-[300px] min-w-	[300px] mr-[10px] mb-[5px]",
			titleStyle: "w-[100px] min-w-[100px] mr-[5px] text-left",
			inputStyle: "w-[110px] min-w-[110px] mr-[5px]",
			timeInputStyle: "w-[80px] min-w-[80px]"
		},
		pickerPos = {top: 0, left: 0, height: 0},
		setPickerPos = () => { console.log('setPickerPos를 호출해서 top, left, height를 설정해야 합니다') },
		resetType = 'today',
		yearMonthVisible = false,
		placeholder = "날짜 및 시간 선택",
		showSeconds = false,
		hideTime = false,
		hidePickerTime = false
	} = props;

	const inputRef = useRef<HTMLInputElement>(null);
	const timeInputRef = useRef<HTMLInputElement>(null);
	const datePickerContainerRef = useRef<HTMLDivElement>(null);
	const [isEditing, setIsEditing] = useState(false);
	const [isTimeEditing, setIsTimeEditing] = useState(false);
	const [inputValue, setInputValue] = useState(formatDate(value));
	const [inputTimeValue, setInputTimeValue] = useState(
		value ? formatTime(value, showSeconds) : '00:00'
	);

	// visible 상태가 변경될 때 달력 컨테이너에 포커스 주기
	useEffect(() => {
		if (visible && datePickerContainerRef.current) {
			datePickerContainerRef.current.focus();
		}
	}, [visible]);

	// value가 변경될 때 inputValue와 inputTimeValue 업데이트
	useEffect(() => {
		setInputValue(formatDate(value));
		setInputTimeValue(value ? formatTime(value, showSeconds) : '00:00');
	}, [value, showSeconds]);

	// 날짜 입력란 클릭 시 위치 계산
	const handleDateInputClick = () => {
		if (!editable) return;
		if (inputRef.current) {
			const rect = inputRef.current.getBoundingClientRect();
			setPickerPos({ top: rect.bottom + 4, left: rect.left, height: rect.height });
		}
		setVisible(true);
	};

	// 시간 입력란 클릭 시
	const handleTimeInputClick = () => {
		if (!editable) return;
		setIsTimeEditing(true);
		if (timeInputRef.current) {
			timeInputRef.current.focus();
		}
	};

	// 날짜 입력란 변경 시 동작
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value);
	};

	// 4자리 숫자를 HH:MM 형식으로 변환 (blur/enter 시에만 사용)
	const formatTimeFromDigits = (digits: string): string => {
		if (!digits || digits.length === 0) return "00:00";
		
		// 4자리로 패딩
		const paddedDigits = digits.padStart(4, '0');
		
		// HH:MM 형식으로 변환
		let hours = parseInt(paddedDigits.slice(0, 2));
		let minutes = parseInt(paddedDigits.slice(2, 4));
		
		// 분 범위 제한 (0-59)
		minutes = Math.min(minutes, 59);
		
		// 시간 범위 제한 (0-23)
		hours = Math.min(hours, 23);
		
		const formattedHours = hours.toString().padStart(2, '0');
		const formattedMinutes = minutes.toString().padStart(2, '0');
		
		return `${formattedHours}:${formattedMinutes}`;
	};

	// 시간 입력란 변경 시 동작 (오른쪽 입력, 왼쪽 글자 하나씩 사라짐)
	const handleInputTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const timeValue = e.target.value;
		
		// 입력값에서 숫자만 추출
		const digits = timeValue.replace(/\D/g, '');
		
		// 오른쪽에서 입력하면서 왼쪽 글자가 하나씩 사라지는 방식
		// 예: "1234" → "2345" (새로운 숫자 5가 추가되면 맨 앞 1이 사라짐)
		let processedDigits = digits;
		if (digits.length > 4) {
			// 4자리를 초과하면 맨 앞부터 제거
			processedDigits = digits.slice(-4);
		}
		
		// 단순히 숫자만 저장 (변환 없음)
		setInputTimeValue(processedDigits);
		
		// 커서를 맨 오른쪽으로 이동
		setTimeout(() => {
			if (timeInputRef.current) {
				timeInputRef.current.setSelectionRange(processedDigits.length, processedDigits.length);
			}
		}, 0);
	};

	// 날짜 입력란 blur 처리
	const handleInputBlur = () => {
		setIsEditing(false);
		// 날짜 형식 검증 (YYYY-MM-DD)
		const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
		if (dateRegex.test(inputValue)) {
			const [year, month, day] = inputValue.split('-').map(Number);
			const newDate = new Date(year, month - 1, day);
			if (!isNaN(newDate.getTime())) {
				// 기존 시간 정보 유지
				if (value) {
					newDate.setHours(value.getHours(), value.getMinutes(), value.getSeconds());
				}
				setValue(newDate);
			} else {
				setInputValue(formatDate(value));
			}
		} else {
			setInputValue(formatDate(value));
		}
	};

	// 시간 입력란 blur 처리 (4자리 입력 시에만 변환)
	const handleTimeInputBlur = () => {
		setIsTimeEditing(false);
		if (value && inputTimeValue.length === 4) {
			// 4자리 숫자를 HH:MM 형식으로 변환
			const formattedValue = formatTimeFromDigits(inputTimeValue);
			setInputTimeValue(formattedValue);
			
			// 실제 Date 객체 업데이트
			const paddedDigits = inputTimeValue.padStart(4, '0');
			let hours = parseInt(paddedDigits.slice(0, 2));
			let minutes = parseInt(paddedDigits.slice(2, 4));
			
			// 분 범위 제한 (0-59)
			minutes = Math.min(minutes, 59);
			
			const newDate = new Date(value);
			newDate.setHours(Math.min(hours, 23), minutes, 0, 0);
			setValue(newDate);
		} else if (value) {
			// 4자리가 아닌 경우 기존 값으로 복원
			setInputTimeValue(formatTime(value, showSeconds));
		}
	};

	// 날짜 입력란 키보드 이벤트
	const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.currentTarget.blur();
			if(visible) {
				setVisible(false);
				handleDatePickerKeyDown();
			}
		}
		if (e.key === 'Tab' && !e.shiftKey && !hideTime) {
			e.preventDefault();
			timeInputRef.current?.focus();
		}
	};

	// 시간 입력란 키보드 이벤트
	const handleTimeInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.currentTarget.blur(); // blur 이벤트로 변환 처리
		}
		if (e.key === 'Tab' && e.shiftKey) {
			e.preventDefault();
			inputRef.current?.focus();
		}
	};

	// 달력 키보드 이벤트
	const handlePickerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key === 'Enter' || e.key === 'Escape') {
			e.preventDefault();
			setVisible(false);
			
			if (e.key === 'Enter') {
				handleDatePickerKeyDown();
			} else {
				inputRef.current?.focus();
			}
		}
	};

	const handleDatePickerKeyDown = () => {
		//입력 포커스 이동을 위한 임시 e : 이벤트 객체
		const syntheticEvent = {
			key: "Enter",
			preventDefault: () => { /* console.log("Synthetic event preventDefault called"); */ },
			currentTarget: inputRef.current,
			target: inputRef.current,
		} as unknown as KeyboardEvent<HTMLInputElement>;

		handleKeyDown(syntheticEvent);
	};

	const onClear = resetType === 'clear' ? () => {
		setValue(undefined);
		setInputValue('');
		setInputTimeValue('');
		setVisible(false);
	} : undefined;


	return (
		<div className={`flex h-[20px] ${wrapperOption.wrapperStyle}`}>
			<p className={`${wrapperOption.titleStyle} form-title ${titleMRClass} cursor-pointer ${titleless ? 'hidden' : ''}`} 
				onClick={() => {
					handleDateInputClick();
				}}
				tabIndex={ editable ? 0 : -1}
			>
				{title}
			</p>
			
			{/* 날짜 입력 영역 */}
			{isEditing ? (
				<input
					ref={inputRef}
					className={`${editable ? 'bg-white' : 'bg-gray-disable'} px-[6px] text-left rounded-[4px] border-[1px] ${wrapperOption.inputStyle}`}
					value={inputValue}
					onChange={handleInputChange}
					onBlur={handleInputBlur}
					onKeyDown={handleInputKeyDown}
					placeholder="YYYY-MM-DD"
					disabled={!editable}
					autoFocus
				/>
			) : (
				<p
					ref={inputRef}
					className={`${editable ? 'bg-white' : 'bg-gray-disable'} px-[6px] text-left rounded-[4px] border-[1px] cursor-pointer ${wrapperOption.inputStyle} overflow-hidden whitespace-nowrap`}
					onClick={handleDateInputClick}
					onDoubleClick={() => {
						setIsEditing(true);
						setInputValue(formatDate(value));
					}}
					tabIndex={ editable ? 0 : -1}
				>
					{value ? formatDate(value) : placeholder}
				</p>
			)}

			{/* 시간 입력 영역 */}
			{!hideTime && (
				<>
					{isTimeEditing ? (
						<input
							ref={timeInputRef}
							className={`${editable ? 'bg-white' : 'bg-gray-disable'} px-[6px] text-left rounded-[4px] border-[1px] ${wrapperOption.timeInputStyle}`}
							value={inputTimeValue}
							onChange={handleInputTimeChange}
							onBlur={handleTimeInputBlur}
							onKeyDown={handleTimeInputKeyDown}
							placeholder={showSeconds ? "1234 → 2345" : "1234 → 2345"}
							disabled={!editable}
							autoFocus
						/>
					) : (
						<p
							ref={timeInputRef}
							className={`${editable ? 'bg-white' : 'bg-gray-disable'} px-[6px] text-left rounded-[4px] border-[1px] cursor-pointer ${wrapperOption.timeInputStyle} overflow-hidden whitespace-nowrap`}
							onClick={handleTimeInputClick}
							onDoubleClick={() => {
								setIsTimeEditing(true);
								setInputTimeValue(value ? formatTime(value, showSeconds) : '00:00');
							}}
							tabIndex={ editable ? 0 : -1}
						>
							{value ? formatTime(value, showSeconds) : "00:00"}
						</p>
					)}
				</>
			)}


			{/* 달력 포털 */}
			{visible && createPortal(
				<>
					{/* 레이아웃 */}
					<div
						className="fixed top-0 left-0 w-screen h-screen z-[99998]"
						onClick={() => setVisible(false)}
					/>
					<div
						ref={datePickerContainerRef}
						style={{
							position: 'fixed', top: pickerPos.top, left: pickerPos.left, zIndex: 99999
						}}
						onClick={e => e.stopPropagation()}
						onKeyDown={handlePickerKeyDown}
						tabIndex={ editable ? 0 : -1}
					>
						<DatePicker
							value={value}
							onChange={(date: Date | undefined) => {
								if(date) {
									// 기존 시간 정보를 완전히 보존하고 날짜만 변경
									if (value) {
										const newDate = new Date(date);
										newDate.setHours(value.getHours(), value.getMinutes(), 0, 0);
										setValue(newDate);
										setInputValue(formatDate(newDate));
										setInputTimeValue(formatTime(newDate, showSeconds));
									} else {
										setValue(date);
										setInputValue(formatDate(date));
										setInputTimeValue(formatTime(date, showSeconds));
									}
								}
							}}
							onReset={() => {
								const today = new Date();
								setValue(today);
								setInputValue(formatDate(today));
								setInputTimeValue(formatTime(today, showSeconds));
								setVisible(false);
							}}
							onConfirm={(date: Date | undefined) => {
								if(date) {
									if(resetType === 'today') {
									// 기존 시간 정보를 완전히 보존하고 날짜만 변경
									if (value) {
										const newDate = new Date(date);
										newDate.setHours(value.getHours(), value.getMinutes(), 0, 0);
										setValue(newDate);
										setInputValue(formatDate(newDate));
										setInputTimeValue(formatTime(newDate, showSeconds));
									} else {
										setValue(date);
										setInputValue(formatDate(date));
										setInputTimeValue(formatTime(date, showSeconds));
									}
									}
									setVisible(false);
									handleDatePickerKeyDown();
								}
							}}
							onClear={onClear}
							yearMonthVisible={yearMonthVisible}
							timeEditable={!hidePickerTime}
						/>
					</div>
				</>,
				document.body
			)}
		</div>
	)
}
